import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { categories, movies } from '@/mocks/movies';
import type { Movie } from '@/mocks/movies';

export default function CategoryPage() {
  const { categoryKey = 'all' } = useParams<{ categoryKey: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'new'>('new');
  const [searchQuery, setSearchQuery] = useState('');

  const currentCategory = categories.find((c) => c.key === categoryKey) || categories[0];

  const filteredMovies = useMemo(() => {
    let result = categoryKey === 'all'
      ? [...movies]
      : movies.filter((m) => m.category === categoryKey);

    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.englishTitle.toLowerCase().includes(q) ||
          m.director.toLowerCase().includes(q) ||
          m.cast.some((c) => c.toLowerCase().includes(q)) ||
          m.tags.some((t) => t.toLowerCase().includes(q))
      );
    }

    if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      result.sort((a, b) => b.year - a.year);
    } else {
      result.sort((a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime());
    }

    return result;
  }, [categoryKey, sortBy, searchQuery]);

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'CollectionPage',
            name: `小草影院${currentCategory.name} - ${currentCategory.name}电影分类大全`,
            description: `小草影院${currentCategory.name}分类，汇集最新${currentCategory.name}在线观看资源。高清画质，每日更新。`,
            url: `https://xiaocaocinema.com/category/${categoryKey}`,
            breadcrumb: {
              '@type': 'BreadcrumbList',
              itemListElement: [
                {
                  '@type': 'ListItem',
                  position: 1,
                  name: '首页',
                  item: 'https://xiaocaocinema.com/',
                },
                {
                  '@type': 'ListItem',
                  position: 2,
                  name: currentCategory.name,
                  item: `https://xiaocaocinema.com/category/${categoryKey}`,
                },
              ],
            },
          }),
        }}
      />

      <div className="min-h-screen pt-16">
        <nav className="px-4 md:px-6 py-4 bg-background-50 border-b border-background-200" aria-label="面包屑导航">
          <div className="max-w-7xl mx-auto flex items-center gap-2 text-sm">
            <Link to="/" className="text-foreground-500 hover:text-primary-500 transition-colors">
              首页
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 text-xs"></i>
            <span className="text-foreground-800 font-medium">{currentCategory.name}</span>
          </div>
        </nav>

        <section className="py-8 md:py-12 px-4 md:px-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col lg:flex-row gap-8">
              <aside className="lg:w-56 flex-shrink-0">
                <div className="lg:sticky lg:top-24">
                  <h3 className="text-base font-heading font-bold text-foreground-800 mb-4">
                    电影分类
                  </h3>
                  <nav className="flex flex-wrap lg:flex-col gap-1.5">
                    {categories.map((cat) => (
                      <Link
                        key={cat.key}
                        to={`/category/${cat.key}`}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-colors whitespace-nowrap ${
                          categoryKey === cat.key
                            ? 'bg-primary-50 text-primary-600 font-medium'
                            : 'text-foreground-600 hover:bg-background-100'
                        }`}
                      >
                        <i className={`${cat.icon} text-sm`}></i>
                        {cat.name}
                      </Link>
                    ))}
                  </nav>
                </div>
              </aside>

              <div className="flex-1">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
                  <div>
                    <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
                      {currentCategory.name}
                    </h1>
                    <p className="text-sm text-foreground-500 mt-1">
                      {currentCategory.key === 'all'
                        ? '浏览小草影院全部电影资源'
                        : `小草影院${currentCategory.name}分类，共 ${filteredMovies.length} 部影片`}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="搜索..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-40 md:w-52 text-sm px-4 py-2 rounded-full bg-background-100 border border-background-200 focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-300 text-foreground-700 placeholder-foreground-400"
                      />
                      <i className="ri-search-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
                    </div>
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'rating' | 'year' | 'new')}
                      className="text-sm px-3 py-2 rounded-full bg-background-100 border border-background-200 text-foreground-700 focus:outline-none focus:border-primary-300 cursor-pointer"
                    >
                      <option value="new">最新上线</option>
                      <option value="rating">评分最高</option>
                      <option value="year">年代最新</option>
                    </select>
                  </div>
                </div>

                {filteredMovies.length === 0 ? (
                  <div className="text-center py-20">
                    <div className="w-16 h-16 mx-auto mb-4 bg-background-100 rounded-full flex items-center justify-center">
                      <i className="ri-search-line text-2xl text-foreground-400"></i>
                    </div>
                    <p className="text-foreground-500 text-sm">没有找到匹配的电影</p>
                    <button
                      onClick={() => setSearchQuery('')}
                      className="mt-3 text-sm text-primary-500 hover:text-primary-600 cursor-pointer"
                    >
                      清除搜索条件
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5" data-product-shop="true">
                    {filteredMovies.map((movie) => (
                      <MovieCard key={movie.id} movie={movie} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

function MovieCard({ movie }: { movie: Movie }) {
  return (
    <Link
      to={`/article/${movie.id}`}
      className="group block bg-background-50 rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={movie.poster}
          alt={`${movie.title} - 小草影院${movie.subCategory}`}
          title={`${movie.title}在线观看 - 小草影院`}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute top-2 left-2">
          <span className="px-2 py-0.5 bg-primary-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
            {movie.subCategory}
          </span>
        </div>
        {movie.isNew && (
          <div className="absolute top-2 right-2">
            <span className="px-2 py-0.5 bg-accent-500 text-background-50 text-xs font-medium rounded-full whitespace-nowrap">
              NEW
            </span>
          </div>
        )}
        <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-background-950/80 to-transparent">
          <div className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-400 text-xs"></i>
            <span className="text-xs text-background-100 font-medium">{movie.rating}</span>
          </div>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-medium text-foreground-900 truncate font-heading">
          {movie.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1 flex flex-wrap items-center gap-2">
          <span>{movie.year}</span>
          <span className="text-foreground-300">|</span>
          <span>{movie.duration}</span>
          <span className="text-foreground-300">|</span>
          <span>{movie.region}</span>
        </p>
      </div>
    </Link>
  );
}